import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MessageSquare, Users, Star, Radio, Plus, ArrowRight, Activity, TrendingUp } from "lucide-react";
import api from "@/lib/apiClient";
import { useSSE } from "@/lib/useSSE";
import { useAuth, hasRole } from "@/context/AuthContext";
import { PageHeader, StatCard, EmptyState, StatusBadge } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function DashboardHome() {
  const { user, role, activeMembership } = useAuth();
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => setStats({}));
  useEffect(() => { if (activeMembership?.org_id) loadStats(); }, [activeMembership?.org_id]);

  const token = localStorage.getItem("pitso_token");
  useSSE(activeMembership?.org_id ? `/stream/org/${activeMembership.org_id}?token=${token}` : null, () => loadStats(), !!activeMembership?.org_id);

  if (!activeMembership) {
    return (
      <div className="p-5 sm:p-8 max-w-2xl">
        <div className="rounded-xl border border-dashed border-border bg-card/50 p-12 text-center" data-testid="no-org-state">
          <h1 className="font-display text-xl font-bold text-foreground">No organisation selected</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {user?.is_super_admin ? "Create an organisation from the Super Admin console to get started." : "You are not a member of any organisation yet."}
          </p>
          {user?.is_super_admin && (
            <a href="/super-admin/organisations" className="inline-block mt-5 rounded-md bg-accent text-accent-foreground px-4 py-2 text-sm font-medium">Go to Super Admin</a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-8 max-w-6xl">
      <PageHeader
        title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle={activeMembership ? `${activeMembership.org_name} workspace` : "Your workspace"}
        action={hasRole(role, "org_admin") && (
          <Button asChild className="rounded-md bg-accent text-accent-foreground hover:opacity-90 transition-opacity" data-testid="dashboard-new-event">
            <Link to="/events/new"><Plus className="h-4 w-4 mr-1.5" /> New Event</Link>
          </Button>
        )}
      />

      {!stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={CalendarDays} label="Total events" value={stats.total_events ?? 0} accent />
            <StatCard icon={Radio} label="Upcoming / Live" value={stats.upcoming_events ?? 0} />
            <StatCard icon={CalendarDays} label="Completed" value={stats.completed_events ?? 0} />
            <StatCard icon={Users} label="Attendees" value={stats.total_attendees ?? 0} />
            <StatCard icon={MessageSquare} label="Questions" value={stats.total_questions ?? 0} />
            <StatCard icon={Activity} label="Poll responses" value={stats.poll_responses ?? 0} />
            <StatCard icon={TrendingUp} label="Participation" value={`${stats.participation_rate ?? 0}%`} accent />
            <StatCard icon={Star} label="Feedback score" value={stats.avg_feedback ? `${stats.avg_feedback}` : "—"} />
          </div>

          <div className="mt-8 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <h2 className="font-display text-lg font-bold text-foreground mb-4">Recent events</h2>
              {stats.recent_events?.length ? (
                <div className="rounded-xl border border-border bg-card divide-y divide-border">
                  {stats.recent_events.map((e) => (
                    <Link key={e.id} to={`/events/${e.id}`} data-testid={`recent-event-${e.id}`}
                      className="flex items-center justify-between p-4 hover:bg-muted/40 transition-colors group">
                      <div>
                        <p className="font-medium text-foreground">{e.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{e.location || "No location"}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={e.status} />
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <EmptyState icon={CalendarDays} title="No events yet"
                  description="Create your first event to start capturing participation."
                  action={hasRole(role, "org_admin") && <Button asChild className="rounded-md"><Link to="/events/new">Create an event</Link></Button>} />
              )}
            </div>

            <div>
              <h2 className="font-display text-lg font-bold text-foreground mb-4">Recent activity</h2>
              <div className="rounded-xl border border-border bg-card p-4">
                {stats.recent_activity?.length ? (
                  <ul className="space-y-3">
                    {stats.recent_activity.map((a) => (
                      <li key={a.id} className="flex items-start gap-2 text-sm">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                        <span className="text-muted-foreground leading-snug">
                          <span className="text-foreground font-medium">{a.user_name || "Someone"}</span> · {a.action?.replace(/[._]/g, " ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-muted-foreground">No activity yet.</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
